import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | verify
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const register = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setStep("verify");
      toast.success("Doğrulama kodu e-posta adresinize gönderildi.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    try {
      await api.post("/auth/resend-code", { email: form.email, password: form.password });
      toast.success("Yeni kod e-posta adresinize gönderildi.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kod gönderilemedi");
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyEmail(form.email, code);
      toast.success("E-posta doğrulandı! Hoş geldiniz.");
      navigate("/tasarim");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-atelier flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <img src="/favicon.png" alt="Taban Mobilya logosu" className="h-12 w-12 object-contain" />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-3xl font-bold uppercase tracking-wide text-stone-900">
              Taban <span className="text-primary">Mobilya</span>
            </span>
            <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-stone-500 mt-1">
              3D Mobilya Atölyesi
            </span>
          </span>
        </Link>

        <div className="card-atelier rounded-2xl p-8">
          {step === "form" ? (
            <>
              <h1 className="font-serif text-3xl font-bold text-stone-900 mb-1">Üye Ol</h1>
              <p className="text-sm text-stone-500 mb-6">E-posta doğrulaması ile güvenli kayıt.</p>
              <form onSubmit={register} className="space-y-4">
                <div>
                  <Label>Ad Soyad</Label>
                  <Input
                    data-testid="register-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Adınız Soyadınız"
                    required
                  />
                </div>
                <div>
                  <Label>E-posta</Label>
                  <Input
                    data-testid="register-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="ornek@eposta.com"
                    required
                  />
                </div>
                <div>
                  <Label>Şifre</Label>
                  <Input
                    data-testid="register-password-input"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="En az 6 karakter"
                    minLength={6}
                    required
                  />
                </div>
                {error && (
                  <p data-testid="register-error" className="text-sm text-red-600 bg-red-50 rounded-lg p-2.5">
                    {error}
                  </p>
                )}
                <Button data-testid="register-submit-button" type="submit" className="w-full rounded-full" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Kayıt Ol
                </Button>
              </form>
              <p className="text-sm text-stone-500 text-center mt-6">
                Zaten üye misiniz?{" "}
                <Link to="/login" className="text-primary font-semibold hover:underline">
                  Giriş yapın
                </Link>
              </p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                <MailCheck className="w-6 h-6" />
              </div>
              <h1 className="font-serif text-3xl font-bold text-stone-900 mb-1">E-postanızı Doğrulayın</h1>
              <p className="text-sm text-stone-500 mb-4">
                <span className="font-semibold">{form.email}</span> adresine 6 haneli bir doğrulama kodu gönderdik.
                Gelen kutunuzu (ve spam klasörünü) kontrol edin.
              </p>
              <form onSubmit={verify} className="space-y-4">
                <div>
                  <Label>Doğrulama Kodu</Label>
                  <Input
                    data-testid="verify-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl font-mono tracking-[0.5em]"
                    required
                  />
                </div>
                {error && (
                  <p data-testid="verify-error" className="text-sm text-red-600 bg-red-50 rounded-lg p-2.5">
                    {error}
                  </p>
                )}
                <Button data-testid="verify-submit-button" type="submit" className="w-full rounded-full" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Doğrula ve Giriş Yap
                </Button>
                <button
                  type="button"
                  data-testid="verify-resend-btn"
                  onClick={resend}
                  className="w-full text-xs text-stone-500 hover:text-primary"
                >
                  Kod gelmedi mi? Tekrar gönder
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
